import {
	AddIcCallOutlined,
	DeleteForeverOutlined,
	KeyboardArrowLeft,
	PersonOutline,
	PhoneOutlined,
	ReplayOutlined,
	RestoreOutlined,
	WarningAmberOutlined
} from '@mui/icons-material';
import {
	Box,
	Button,
	Card,
	CardContent,
	CardMedia,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	IconButton,
	keyframes,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
	useTheme
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import phone from 'phone';
import { useContext, useEffect, useState } from 'react';
import { ActivityLog } from '../../../components/contacts/activityLog';
import { CallStatusPane } from '../../../components/engagement/callStatusPane';
import { UserContext } from '../../../contexts/UserContext';
import { getContact as getContactQuery } from '../../../graphql/queries';
import { tabStyle } from '../../../theme';
import { ContactInfoPane } from './contactInfoPane';
import { CustomFieldsPane } from './customFieldsPane';

const fadeIn = keyframes`
	from { opacity: 0; }
	to   { opacity: 1; }
`;

const squishBounce = keyframes`
	0%   { transform: scale(1); box-shadow: 0 0 0 0 currentColor; }
	10%  { transform: scale(1.15, 0.8); }
	20%  { transform: scale(0.9, 1.08); }
	30%  { transform: scale(1.04, 0.96); }
	40%  { transform: scale(1); }
	70%  { box-shadow: 0 0 0 8px transparent; }
	100% { transform: scale(1); box-shadow: 0 0 0 0 transparent; }
`;

/**
 * The list of phone fields to display. Computed once.
 */
const phoneFields = [
	'phone',
	'cell'
];

/**
 * @param {{
 *   contact: Contact
 *   callStatus: string
 *   allowManualNumberEntry: boolean
 *   allowMultipleCallsPerContact: boolean
 *   allowRedial: boolean
 *   dismissMode: 'back' | 'dismiss'
 *   onDismiss: (phoneFieldsAlreadyCalled) => {}
 *   onRecycle: () => {}
 *   onSuppress: () => {}
 *   onApprove: (phoneNumber) => {}
 *   onResetApprove: () => {}
 * }} props
 */
export function ContactPreview(props) {
	const theme = useTheme();

	const style = {
		animation: `${fadeIn} .5s`,
	};

	/** The user context. */
	const userContext = useContext(UserContext);

	/** The GraphQL client. */
	const graphqlClient = generateClient();

	/**
	 * The list of phone custom fields (additional phone numbers) to display.
	 */
	const [phoneCustomFields, setPhoneCustomFields] = useState({});

	/** The contact being previewed. */
	const [contact, setContact] = useState(props.contact);

	/**
	 * The list of phone fields for the contact that have already been called.
	 */
	const [phoneFieldsAlreadyCalled, setPhoneFieldsAlreadyCalled] = useState([]);

	/** Whether the contact details are loading. */
	const [isContactLoading, setIsContactLoading] = useState(false);

	/** The index of the currently selected tab. */
	const [currentTab, setCurrentTab] = useState(0);

	/** Whether the manual dial dialog is open. */
	const [isManualDialDialogOpen, setIsManualDialDialogOpen] = useState(false);

	/** The manually entered phone number. */
	const [manualNumber, setManualNumber] = useState(phone(''));

	/** Which action button is currently animating (for squish-bounce feedback). */
	const [bouncingAction, setBouncingAction] = useState(null);

	// Whenever the contact prop changes, get the full contact from DynamoDB.
	useEffect(() => {
		const getFullContact = async () => {
			const fullContact = await getContact(props.contact.id);
			setContact({
				...fullContact,
				outboundCallerId: props.contact.outboundCallerId || fullContact?.outboundCallerId || null,
			});
		};
		getFullContact();
	}, [props.contact]);

	// Whenever the contact changes, update the phone custom fields.
	useEffect(() => {
		let contactCustomFields = {}
		// Only parse if type is string
		if (typeof contact?.customFields === 'string') {
			contactCustomFields = JSON.parse(contact.customFields);
		} else {
			contactCustomFields = contact?.customFields || {}
		}

		// Only include the phone custom fields.
		const phoneCustomFieldDefinitions = userContext.customFields.filter((field) => field.type === 'Phone');
		const filteredCustomFields = {};
		Object.entries(contactCustomFields).map(([key, value]) => {
			if (phoneCustomFieldDefinitions.some((fieldDefinition) => fieldDefinition.name === key)) {
				filteredCustomFields[key] = value;
			}
		});
		setPhoneCustomFields(filteredCustomFields);
	}, [contact]);

	/**
	 * Gets the label to display for a phone field.
	 *
	 * @param {string} phoneField The name of the phone field. Anything other than 'phone' or 'cell' is considered a custom field.
	 * @returns {string} The label to display for the phone field. Falls back to the phone field name if a custom field display name is not found.
	 */
	function getPhoneLabel(phoneField) {
		switch (phoneField) {
			case 'phone':
				return 'Phone';
			case 'cell':
				return 'Cell';
			default: {
				const match = userContext.customFields.find(
					(customField) => customField.name === phoneField,
				);
				return match?.displayName ?? phoneField;
			}
		}
	}

	/**
	 * Formats a phone number to be in the format +# (###) ###-####.
	 *
	 * @param {string} phoneNumber The phone number to format.
	 * @returns A formatted phone number string.
	 */
	function getFormattedPhoneNumber(phoneNumber) {
		try {
			const cleaned = ('' + phoneNumber).replace(/\D/g, '');
			const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
			const intlCode = match[1] ? '+1 ' : '';
			return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
		} catch (error) {
			return phoneNumber;
		}
	}

	/**
	 * Gets the full contact details from DynamoDB. Necessary because the contact returned
	 * from IoT is missing some fields.
	 *
	 * @param {string} contactId The ID of the contact to get.
	 * @returns {Promise<Contact>} The full contact details.
	 */
	async function getContact(contactId) {
		setIsContactLoading(true);
		const graphqlOptions = {
			query: getContactQuery,
			variables: {
				id: contactId,
			},
		};
		if (userContext.cognitoToken) {
			graphqlOptions.authMode = 'userPool';
			graphqlOptions.authToken = userContext.cognitoToken;
		}
		const contactRequest = await graphqlClient.graphql(graphqlOptions);
		setIsContactLoading(false);
		return contactRequest.data.getContact;
	}

	/**
	 * Reports that the contact was approved for dialing.
	 *
	 * @param {string} phoneFieldName The name of the phone field being dialed.
	 * @param {string} phoneNumber The phone number being dialed.
	 */
	async function approveForDial(phoneFieldName, phoneNumber) {
		if (!props.allowRedial) {
			setPhoneFieldsAlreadyCalled((prev) => [...prev, phoneFieldName]);
		}
		triggerBounce(`dial-${phoneFieldName}`);
		props.onApprove(phoneNumber);
	}

	/**
	 * Triggers a squish-bounce animation on the given action key.
	 * The animation loops until clearBounce() is called (e.g. when the process completes).
	 */
	function triggerBounce(actionKey) {
		setBouncingAction(actionKey);
	}

	/** Clears the bouncing animation (called when processing completes). */
	function clearBounce() {
		setBouncingAction(null);
	}

	// Clear bounce when callStatus updates (meaning the action was acknowledged)
	useEffect(() => {
		if (props.callStatus) {
			clearBounce();
		}
	}, [props.callStatus]);

	// Clear bounce when contact changes (dismiss/recycle/suppress completed)
	useEffect(() => {
		clearBounce();
	}, [props.contact?.id]);

	/** Returns the sx prop for a bouncing button — loops until cleared. */
	function bounceStyle(actionKey) {
		return bouncingAction === actionKey
			? { animation: `${squishBounce} 1.4s ease infinite`, color: 'primary.main' }
			: {};
	}

	return (
		<>
			<Box
				padding="10px"
				width="100%"
				height="100%"
				sx={{
					overflowY: 'auto',
					backgroundColor: 'transparent',
					...style,
				}}
			>
				{/* Top Card */}
				<Card elevation={0} sx={{ borderRadius: '20px', boxShadow: '0 2px 12px 0 rgba(0,0,0,0.08)', border: '1px solid', borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}>
					{/* Call Status Pane */}
					<CardMedia sx={{ height: '150px', backgroundColor: theme.palette.mode === 'dark' ? '#3b5998ff' : '#a8c8ffff' }}>
						<Box height="100%">
							{props.callStatus && (
								<CallStatusPane
									contact={{ callStatus: props.callStatus }}
									connectionLost={props.connectionLost}
									onDismissCallStatus={() => {
										props.onResetApprove();
									}}
								/>
							)}
						</Box>
					</CardMedia>

					{/* Contact Header */}
					<CardContent>
						<Box
							display="grid"
							gridTemplateColumns="1fr 2fr"
							gap={4}
						>
							{/* Contact Name & ID */}
							<Box
								display="flex"
								flexDirection="column"
								alignItems="flex-start"
							>
								{props.onDismiss && (props.dismissMode !== 'dismiss' || phoneFieldsAlreadyCalled.length > 0) && <Button
									onClick={() => {
										props.onDismiss(phoneFieldsAlreadyCalled);
									}}
									size='small'
									sx={{
										marginBottom: '10px',
										marginLeft: '-10px',
									}}
									disabled={!!props.callStatus}
								>
									<KeyboardArrowLeft
										color="primary"
										sx={{
											opacity: props.callStatus ? 0.25 : 1,
										}}
									/>
									{props.dismissMode === 'dismiss' ? 'Dismiss' : 'Back'}
								</Button>}

								{/* Name */}
								<Typography variant="h4">
									{contact?.firstName} {contact?.lastName}
								</Typography>

								{/* External ID */}
								{contact?.externalId && (
									<Typography variant="subtitle" gutterBottom>{`External ID: ${contact.externalId}`}</Typography>
								)}

								{/* Contact Actions */}
								<Box
									display="flex"
									flexDirection="row"
									gap={2}
									justifyContent="flex-start"
									alignItems="center"
								>

									{/* Recycle Contact */}
									{props.onRecycle && <Tooltip title={phoneFieldsAlreadyCalled.length < 1 && !props.callStatus ? "Recycle the contact to be presented again later" : "Unable to recycle contact that has already been called"} arrow={true}>
										<span>
											<IconButton
												onClick={() => {
													triggerBounce('recycle');
													props.onRecycle();
												}}
												disabled={phoneFieldsAlreadyCalled.length > 0 || !!props.callStatus}
												sx={{
													...bounceStyle('recycle'),
													'&:disabled': {
														opacity: 0.25,
													},
												}}
											>
												<ReplayOutlined color="primary" />
											</IconButton>
										</span>
									</Tooltip>}

									{/* Suppress Contact */}
									{props.onSuppress && <Tooltip title={phoneFieldsAlreadyCalled.length < 1 && !props.callStatus ? "Suppress the contact from being presented again" : "Unable to suppress contact that has already been called"} arrow={true}>
										<span>
											<IconButton
												onClick={() => {
													triggerBounce('suppress');
													props.onSuppress();
												}}
												disabled={phoneFieldsAlreadyCalled.length > 0 || !!props.callStatus}
												sx={{
													...bounceStyle('suppress'),
													'&:disabled': {
														opacity: 0.25,
													},
												}}
											>
												<DeleteForeverOutlined
													color="primary"
												/>
											</IconButton>
										</span>
									</Tooltip>}

									{/* Manual Dial */}
									{props.allowManualNumberEntry && (
										<Tooltip
											title={
												!(
													!!props.callStatus ||
													(!props.allowMultipleCallsPerContact && phoneFieldsAlreadyCalled.length > 0)
												) ? "Manually dial a number for this contact" : ""}
											arrow={true}
										>
											<span>
												<IconButton
													onClick={() => setIsManualDialDialogOpen(true)}
													disabled={
														!!props.callStatus ||
														(!props.allowMultipleCallsPerContact && phoneFieldsAlreadyCalled.length > 0)
													}
													sx={{
														'&:disabled': {
															opacity: 0.25,
														},
													}}
												>
													<AddIcCallOutlined color="primary" />
												</IconButton>
											</span>
										</Tooltip>
									)}

								</Box>
							</Box>

							{/* Phone Numbers */}
							<Box display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={3}>

								{phoneFields.map((field) => {
									if (contact[field]) {
										return (
											<Card key={field} elevation={0} sx={{ padding: '10px', borderRadius: '40px', backgroundColor: theme.palette.mode === 'dark' ? '#2e3d5fff' : '#edf3fcff', border: '1px solid', borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }}>
												<Box display="grid" gridTemplateColumns="auto auto" gap={1} sx={{ justifyItems: 'center', alignItems: 'center' }}>
													<Tooltip title={phoneFieldsAlreadyCalled.includes(field) ? "This number has already been dialed" : ""} arrow={true}>
														<span>
															<IconButton
																sx={[{ backgroundColor: theme.palette.primary.main, ...bounceStyle(`dial-${field}`) }, {
																	'&:hover': {
																		backgroundColor: theme.palette.primary.dark,
																	},
																	'&:disabled': {
																		backgroundColor: theme.palette.action.disabledBackground,
																	},
																}]}
																onClick={() => approveForDial(field, contact[field])}
																disabled={
																	!!props.callStatus ||
																	phoneFieldsAlreadyCalled.includes(field) ||
																	(!props.allowMultipleCallsPerContact && phoneFieldsAlreadyCalled.length > 0)
																	|| !props.onApprove
																}
															>
																<PhoneOutlined color="white" />
															</IconButton>
														</span>
													</Tooltip>
													<div>
														<Typography variant="caption">
															{getPhoneLabel(field)}
														</Typography>
														<Typography variant="body1" sx={{ fontWeight: 'medium' }}>
															{getFormattedPhoneNumber(
																contact[field]
															)}
														</Typography>
													</div>
												</Box>
											</Card>
										);
									}
								})}

							</Box>

						</Box>

						{/* Additional Phone Numbers */}
						{Object.keys(phoneCustomFields).length > 0 &&
							<>
								<Divider sx={{ marginTop: '20px', marginBottom: '20px' }}></Divider>
								<Typography variant="subtitle2" sx={{ marginTop: '10px' }} gutterBottom>
									Additional Phone Numbers
								</Typography>
								<Box display="flex" justifyContent="flex-start" alignItems="center" flexWrap="wrap" gap={6}>
									{Object.entries(phoneCustomFields).map(([field, value]) => {
										return (
											<Box key={field} display="grid" gridTemplateColumns="auto auto" gap={1} sx={{ justifyItems: 'center', alignItems: 'center' }}>
												<Tooltip title={phoneFieldsAlreadyCalled.includes(field) ? "This number has already been dialed" : ""} arrow={true}>
													<span>
														<IconButton
															onClick={() => approveForDial(field, value)}
															disabled={
																!!props.callStatus ||
																phoneFieldsAlreadyCalled.includes(field) ||
																(!props.allowMultipleCallsPerContact && phoneFieldsAlreadyCalled.length > 0)
																|| !props.onApprove
															}
															sx={{
																...bounceStyle(`dial-${field}`),
																'&:disabled': {
																	opacity: 0.25,
																},
															}}
														>
															<PhoneOutlined color="primary" />
														</IconButton>
													</span>
												</Tooltip>
												<div>
													<Typography variant="caption">
														{getPhoneLabel(field)}
													</Typography>
													<Typography variant="body1">
														{getFormattedPhoneNumber(value)}
													</Typography>
												</div>
											</Box>
										);
									})}
								</Box>
							</>
						}

					</CardContent>

					{contact && (
						<>
							{/* Tabs */}
							<Box
								display="flex"
								flexDirection="row"
								sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#2e3d5fff' : '#f0f4faff' }}
							>
								<Tabs
									value={currentTab}
									onChange={(_, newValue) => setCurrentTab(newValue)}
									indicatorColor="primary"
									textColor="primary"
									variant="scrollable"
									scrollButtons="auto"
								>
									<Tab
										iconPosition="start"
										icon={<PersonOutline />}
										sx={tabStyle}
										label="Contact Info"
									></Tab>
									<Tab
										iconPosition="start"
										icon={<RestoreOutlined />}
										sx={tabStyle}
										label="Activity Log"
									></Tab>
								</Tabs>
							</Box>

							{/* Contact Info */}
							<CardContent>
								{currentTab === 0 && (
									<>
										<ContactInfoPane contact={contact} />
										<Divider sx={{ marginTop: '30px', marginBottom: '30px' }}></Divider>
										<CustomFieldsPane contact={contact} />
									</>
								)}
								{currentTab === 1 && (
									<>
										<ActivityLog contactId={contact.id} />
										{userContext.telephonyProvider === 'NiC' && (
											<Box display="flex" alignItems="center" justifyContent="center" gap={1} padding="10px">
												<WarningAmberOutlined color="warning" size="small" />
												<Typography variant="body2" color="GrayText">
													Due to how NICE handles dispositioning, activity log suppression is not guaranteed to be accurate.
												</Typography>
											</Box>
										)}
									</>
								)}
							</CardContent>
						</>
					)}
				</Card>
			</Box>

			<Dialog
				open={isManualDialDialogOpen}
				size="md"
			>
				<DialogTitle>Manual Dial</DialogTitle>
				<DialogContent>
					<DialogContentText gutterBottom>Enter a phone number to be dialed for this contact.</DialogContentText>
					<TextField
						inputProps={{ id: 'manual-dial-phone-input' }}
						id="manual-dial-phone-input"
						type='tel'
						value={manualNumber.phoneNumber}
						onChange={(e) => {
							setManualNumber(phone(e.target.value));
						}}
						label="Phone Number"
						variant="outlined"
						autoComplete='off'
						fullWidth
					/>
				</DialogContent>
				<DialogActions>
					<Button
						sx={{ color: theme.palette.action.active }}
						onClick={() => {
							setIsManualDialDialogOpen(false);
							setManualNumber(phone(''));
						}}
					>
						Cancel
					</Button>
					<Button
						disabled={manualNumber.isValid === false}
						onClick={() => {
							approveForDial('manual', manualNumber.phoneNumber);
							setIsManualDialDialogOpen(false);
							setManualNumber(phone(''));
						}}
					>
						Dial
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
